import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  v2GetPortfolioOutreachStatusOptions,
  v2SelectStartupOutreachChannelsMutation,
} from '@workspace/api-backend/react-query'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { Textarea } from '@workspace/ui/components/textarea'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowUpRight, Check, Pencil } from 'lucide-react'
import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { trackEvent } from '../../../lib/gtag'
import { useStartupFeatureFlagsQuery } from '../../feature-flags/hooks/use-startup-feature-flags'
import { outreachStatusQueryKey } from '../api'
import { LINKEDIN_SAFETY_HELP_URL } from '../config/launch-checklist'
import {
  estimateMsg1LinkLength,
  estimateMsg1SentLength,
  MSG1_MAX_CHARS,
} from '../config/message-limits'
import { useLaunchFlow } from '../context/launch-flow-context'
import { useOutreachMessagesQuery } from '../hooks/use-outreach-messages'
import { useResetOutreachMessageMutation } from '../hooks/use-reset-outreach-message-mutation'
import { useReviewOutreachMessagesMutation } from '../hooks/use-review-outreach-messages-mutation'
import { useUpdateOutreachMessagesMutation } from '../hooks/use-update-outreach-messages-mutation'
import { resolveShownArm } from '../lib/message-variants'
import {
  blockingReviewSequence,
  canApproveReview,
  type SequenceValidity,
} from '../lib/review-validity'
import type { OutreachMessageVariant } from '../types'
import {
  decorateLegacyOutreachPreview,
  fundTokensFromProfile,
  isLegacyOutreachField,
  type OutreachMessageField,
  renderTemplatePreview,
} from '../utils/render-template-preview'
import { ChannelSwitcher } from './channel-switcher'
import { MessagesPreparing } from './messages-preparing'
import type { RosterMatch } from './outreach-active-view'
import { VariantToggle } from './outreach-variant-toggle'
import { WarmupSteps } from './outreach-warmup-steps'
import {
  PortfolioReviewBody,
  type PortfolioReviewBodyHandle,
} from './portfolio-review-messages-dialog'

type ReviewTab = 'investors' | 'portfolio'

type Drafts = { msg1: string; msg2: string; msg3: string }
type MessageKey = keyof Drafts

// The "Review your messages" launch step: real matched contacts on the left,
// the conversation each one will receive on the right. Switching contacts
// re-personalizes the preview; editing a bubble edits the underlying template
// (one sequence for everyone — only the greeting and link are per-contact).
// The primary action saves any edits AND marks the messages reviewed.
export function OutreachReviewMessagesDialog({
  startupId,
  open,
  onOpenChange,
  contacts,
  totalContacts,
  contactsLabel,
  initialTab = 'investors',
}: {
  startupId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Best-fit contacts (from matches), used for the preview switcher. */
  contacts: RosterMatch[]
  totalContacts: number
  /** Contact-count display string ("85" / "~85") for copy. */
  contactsLabel: string
  /** Which channel's messages to open on (portfolio "Edit messages" passes 'portfolio'). */
  initialTab?: ReviewTab
}) {
  // Once the review gate is stamped, revisits (pre- or post-launch "Edit
  // messages") are plain edits — same dialog, edit-mode framing. Cache-shared
  // with the body's query, so this adds no extra request.
  // `true` = ask the server to fill any missing versions — the founder opening
  // this dialog IS the touchpoint generation is tied to. Gated on `open`: the
  // dialog is rendered by the outreach page whether or not it is showing.
  const messagesQuery = useOutreachMessagesQuery(startupId, true, open)
  const reviewed = messagesQuery.data?.messagesReviewedAt != null

  // Both channels run by default. Whenever the portfolio feature is on we show
  // both tabs so the founder always sees (and can Skip) each channel — the
  // portfolio editor seeds from default templates even before its messages are
  // generated, so the tab never depends on portfolio data being ready.
  const flagsQuery = useStartupFeatureFlagsQuery(startupId)
  const showTabs = flagsQuery.data?.portfolioOutreach ?? false

  const [tab, setTab] = useState<ReviewTab>(initialTab)
  const activeTab: ReviewTab = showTabs ? tab : 'investors'

  // The Disable toggle on each tab is in-memory only — the outreach row (where
  // channel flags live) isn't created until provisioning, so the founder's
  // choice lives in the launch-flow context (shared with the pre-launch tab bar)
  // and is persisted via selectChannels when they approve a tab.
  const queryClient = useQueryClient()
  const { channelEnabled } = useLaunchFlow()

  const investorRef = useRef<ReviewBodyHandle>(null)
  const portfolioRef = useRef<PortfolioReviewBodyHandle>(null)

  // Persist the local channel choice. Called once on the single approve.
  const selectChannels = useMutation(v2SelectStartupOutreachChannelsMutation())

  const [approving, setApproving] = useState(false)
  // Validity is reported UP from each body so the Save button re-evaluates as the
  // founder edits. Reading `ref.current?.canApprove` during render is not
  // reactive — the parent wouldn't re-render when a child's draft changes, so the
  // button's disabled state would go stale (edits appear to do nothing).
  // null = the messages have not loaded yet, so there is nothing to validate.
  // Distinct from false on purpose: a founder must not be told to "fix" a
  // sequence they have not been shown.
  const [investorValid, setInvestorValid] = useState<SequenceValidity>(null)
  const [portfolioValid, setPortfolioValid] = useState<SequenceValidity>(null)
  const handleApproveAll = async () => {
    setApproving(true)
    try {
      await investorRef.current?.approve()
      if (showTabs && channelEnabled('portfolio')) {
        await portfolioRef.current?.approve()
      }
      await new Promise<void>((resolve, reject) =>
        selectChannels.mutate(
          {
            path: { id: startupId },
            body: { investor: channelEnabled('investors'), portfolio: channelEnabled('portfolio') },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: outreachStatusQueryKey(startupId) })
              queryClient.invalidateQueries({
                queryKey: v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }).queryKey,
              })
              resolve()
            },
            onError: reject,
          }
        )
      )
      onOpenChange(false)
    } catch {
      // errors surfaced by individual mutations
    } finally {
      setApproving(false)
    }
  }

  const portfolioActive = showTabs && channelEnabled('portfolio')
  const canApprove = canApproveReview({
    investor: investorValid,
    portfolio: portfolioValid,
    portfolioActive,
  })
  // Name the tab that's blocking Save so the founder isn't stuck editing the
  // wrong sequence (the button gates on BOTH tabs when portfolio is enabled).
  const blockingTab = blockingReviewSequence({
    investor: investorValid,
    portfolio: portfolioValid,
    portfolioActive,
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl lg:max-w-5xl">
        <DialogHeader className="border-b border-border/40 px-6 py-5 pr-12 text-left">
          <DialogTitle>{reviewed ? 'Edit your messages' : 'Review your messages'}</DialogTitle>
          <DialogDescription>
            {reviewed
              ? `This three-message sequence goes to all ${contactsLabel} contacts, personalized with each partner's first name. Changes apply to everyone we haven't messaged yet.`
              : `This three-message sequence goes to all ${contactsLabel} contacts, personalized with each partner's first name and your profile link. Pick a contact to preview their conversation, and edit anything. Nothing sends without your approval.`}
          </DialogDescription>
          <a
            href={LINKEDIN_SAFETY_HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:text-primary/80"
          >
            What SeedForge does with your LinkedIn
            <ArrowUpRight className="size-3" />
          </a>
        </DialogHeader>

        {showTabs && (
          <div className="border-b border-border/40 px-6 py-4">
            <ChannelSwitcher active={activeTab} onSelect={setTab} />
          </div>
        )}

        {/* Both bodies stay mounted so their refs remain live for the unified
            approve handler — whichever tab is inactive is simply hidden. */}
        <ReviewBody
          ref={investorRef}
          startupId={startupId}
          contacts={contacts.filter((c) => c.contactName)}
          totalContacts={totalContacts}
          contactsLabel={contactsLabel}
          hidden={!open || activeTab !== 'investors'}
          onValidityChange={setInvestorValid}
        />
        {showTabs && (
          <PortfolioReviewBody
            ref={portfolioRef}
            startupId={startupId}
            hidden={!open || activeTab !== 'portfolio'}
            onValidityChange={setPortfolioValid}
          />
        )}

        <DialogFooter className="flex-col gap-3 border-t border-border/40 px-6 py-4 sm:flex-row sm:items-center">
          <p className="flex-1 text-[11.5px] sm:text-left">
            {blockingTab ? (
              <span className="font-medium text-destructive">
                Fix the “{blockingTab}” sequence to save. A message is over LinkedIn's{' '}
                {MSG1_MAX_CHARS}-char connection-note limit or empty.
              </span>
            ) : (
              <span className="text-muted-foreground">
                One sequence for everyone. Edits apply to every contact not yet messaged.
              </span>
            )}
          </p>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={approving}>
            Cancel
          </Button>
          <Button onClick={handleApproveAll} disabled={!canApprove || approving}>
            <Check className="size-4" strokeWidth={2.5} />
            {approving ? 'Saving…' : reviewed ? 'Save changes' : 'Approve messages'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export interface ReviewBodyHandle {
  approve: () => Promise<void>
  canApprove: boolean
}

const ReviewBody = forwardRef<
  ReviewBodyHandle,
  {
    startupId: string
    contacts: RosterMatch[]
    totalContacts: number
    contactsLabel: string
    hidden?: boolean
    onValidityChange?: (valid: SequenceValidity) => void
  }
>(function ReviewBody({ startupId, contacts, totalContacts, hidden, onValidityChange }, ref) {
  // See the outer component: gated on visibility, because this body stays
  // mounted while the dialog is closed.
  const messagesQuery = useOutreachMessagesQuery(startupId, true, !hidden)
  const update = useUpdateOutreachMessagesMutation(startupId)
  const review = useReviewOutreachMessagesMutation(startupId)
  const resetMessage = useResetOutreachMessageMutation(startupId)
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [editing, setEditing] = useState<MessageKey | null>(null)
  // Which stored version each message is showing.
  const [shownArm, setShownArm] = useState<Partial<Record<MessageKey, string>>>({})
  /**
   * Text the founder has actually typed, per field — and the ONLY thing Save
   * sends.
   *
   * Kept apart from what is on screen on purpose. Loading a version's text into
   * a shared draft (the obvious implementation) means a founder who opens the
   * toggle to see version B and then presses Save — the same button that
   * satisfies the review gate — has B saved as their own copy, which ends the
   * A/B for that message and pins them to an arm they only glanced at. Looking
   * must never write.
   */
  const [edits, setEdits] = useState<Partial<Record<MessageKey, string>>>({})

  const variantsFor = (field: MessageKey): OutreachMessageVariant[] => {
    const stored = messagesQuery.data?.variants?.[field] ?? []
    if (edits[field] === undefined || stored.some((v) => v.arm === 'custom')) return stored
    // An unsaved edit is shown as "Yours" so switching away from it does not
    // make it disappear.
    return [
      ...stored,
      { id: `local-custom-${field}`, arm: 'custom', text: edits[field] ?? '', promptVersion: null },
    ]
  }

  /** Whether this field carries a founder edit — saved, or typed just now. */
  const hasCustom = (field: MessageKey): boolean =>
    variantsFor(field).some((v) => v.arm === 'custom')

  const effectiveArm = (field: MessageKey): string =>
    resolveShownArm(variantsFor(field), edits[field] !== undefined ? 'custom' : shownArm[field])

  /** What is on screen for a field: the founder's edit, else the shown version. */
  const textFor = (field: MessageKey): string => {
    const arm = effectiveArm(field)
    if (arm === 'custom' && edits[field] !== undefined) return edits[field] ?? ''
    const variant = variantsFor(field).find((v) => v.arm === arm)
    return variant?.text ?? messagesQuery.data?.[field] ?? ''
  }

  const drafts: Drafts | null = messagesQuery.data
    ? { msg1: textFor('msg1'), msg2: textFor('msg2'), msg3: textFor('msg3') }
    : null

  /**
   * Go back to the generated copy for a field: forget any unsaved typing and
   * retire the saved custom version server-side.
   */
  const revertToGenerated = async (field: MessageKey) => {
    setEdits((prev) => {
      const next = { ...prev }
      delete next[field]
      return next
    })
    setShownArm((prev) => ({ ...prev, [field]: 'control' }))
    if (messagesQuery.data?.variants?.[field]?.some((v) => v.arm === 'custom')) {
      await resetMessage.mutateAsync(field)
    }
  }

  /** Switch which version is displayed. Never touches what will be saved. */
  const selectArm = (field: MessageKey, arm: string) => {
    setShownArm((prev) => ({ ...prev, [field]: arm }))
  }

  // Legacy msg2 carries its closing CTA in a separate field; fold it into the
  // msg2 preview when the message is a legacy row.
  const msg2Cta = messagesQuery.data?.msg2Cta ?? null
  const alreadyReviewed = messagesQuery.data?.messagesReviewedAt != null
  const hasEmpty = !drafts?.msg1.trim() || !drafts?.msg2.trim() || !drafts?.msg3.trim()
  const msg1LinkLen = estimateMsg1LinkLength()
  // msg1 carries [firstName]/[FundName]/[FundFocus]/[Link] tokens inline; count
  // its expanded length (every token swapped for a runtime estimate) against the
  // LinkedIn connection-note cap so the warning fires before a substituted note
  // overflows.
  const msg1Total = estimateMsg1SentLength(drafts?.msg1 ?? '')
  const msg1Over = msg1Total > MSG1_MAX_CHARS

  const approve = () =>
    new Promise<void>((resolve, reject) => {
      if (!drafts) {
        reject(new Error('drafts not loaded'))
        return
      }

      const markReviewed = () => {
        if (alreadyReviewed) {
          resolve()
          return
        }
        review.mutate(undefined, {
          onSuccess: () => {
            trackEvent('outreach_messages_reviewed', { startup_id: startupId })
            resolve()
          },
          onError: reject,
        })
      }

      // Send ONLY what the founder typed. Sending all three would turn every
      // field they merely looked at into their own version, which silently ends
      // the A/B for it — approving is not editing.
      if (Object.keys(edits).length === 0) {
        markReviewed()
        return
      }

      update.mutate(edits, {
        onSuccess: () => {
          trackEvent('outreach_messages_saved', { startup_id: startupId })
          markReviewed()
        },
        onError: reject,
      })
    })

  // `approve` closes over the founder's unsaved edits, so the handle is keyed on
  // `edits` as well as `drafts` — otherwise the parent's Save button holds a
  // stale closure and submits the previous keystroke's text.
  useImperativeHandle(ref, () => ({ approve, canApprove: !hasEmpty && !msg1Over }), [
    drafts,
    edits,
    hasEmpty,
    msg1Over,
  ])

  // Report validity up so the parent's Save button re-evaluates on every edit.
  // Nothing loaded yet means nothing to validate — report unknown, not invalid.
  // `hasEmpty` reads a null draft as "empty", which would otherwise flag every
  // startup as broken for as long as generation takes.
  const valid = messagesQuery.data ? !hasEmpty && !msg1Over : null
  useEffect(() => onValidityChange?.(valid), [onValidityChange, valid])

  if (messagesQuery.isError) {
    return (
      <p className={cn('py-10 text-center text-[13px] text-muted-foreground', hidden && 'hidden')}>
        Couldn't load your messages. Close and try again.
      </p>
    )
  }

  if (!drafts) return <MessagesPreparing hidden={hidden} />

  const selected = contacts[Math.min(selectedIdx, Math.max(contacts.length - 1, 0))] ?? null
  const firstName = (selected?.contactName ?? '').split(' ')[0] || 'there'
  // Resolve [FundName]/[FundFocus]/[FundSector] to the selected fund's real name
  // + sector/stage so the preview reads like the actual message (the [Link]
  // token stays a placeholder).
  const resolve = {
    firstName,
    fundName: selected?.investorName || undefined,
    ...fundTokensFromProfile(selected?.profile?.stages, selected?.profile?.sectors),
  }
  const moreCount = Math.max(totalContacts - contacts.length, 0)

  /** Typing is what creates the founder's own version — nothing else does. */
  const setDraft = (key: MessageKey, value: string) =>
    setEdits((prev) => ({ ...prev, [key]: value }))
  const toggleEdit = (key: MessageKey) => setEditing((prev) => (prev === key ? null : key))

  return (
    <div
      className={cn(
        'grid min-h-0 flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[260px_1fr] md:overflow-hidden',
        hidden && 'hidden'
      )}
    >
      {/* ── Contact switcher ── */}
      <aside className="border-b border-border/40 md:overflow-y-auto md:border-b-0 md:border-r">
        <div className="px-4 pb-2 pt-4 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          Preview as · best-fit first
        </div>
        <div className="flex gap-1.5 overflow-x-auto px-3 pb-3 md:flex-col md:overflow-visible">
          {contacts.map((contact, i) => {
            const isSelected = i === selectedIdx
            return (
              <button
                key={contact.leadId ?? `${contact.investorId}-${i}`}
                type="button"
                onClick={() => setSelectedIdx(i)}
                className={cn(
                  'flex w-56 shrink-0 items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors md:w-auto',
                  isSelected
                    ? 'border-primary/50 bg-primary/10'
                    : 'border-transparent hover:bg-card/60'
                )}
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border/40 bg-background/60 text-[11px] font-bold text-foreground/80">
                  {makeInitials(contact.contactName ?? '?')}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[12.5px] font-semibold text-foreground">
                    {contact.contactName}
                  </span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {[contact.contactTitle, contact.investorName].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
        {moreCount > 0 && (
          <p className="px-4 pb-4 text-[11px] leading-relaxed text-muted-foreground/80">
            + {moreCount} more contacts get the same sequence, personalized the same way.
          </p>
        )}
      </aside>

      {/* ── Conversation preview ── */}
      <div className="space-y-3 px-5 py-5 md:overflow-y-auto">
        <WarmupSteps />

        <MessageBubble
          label="Connection request"
          hint={
            <span
              className={cn(
                'tabular-nums',
                msg1Over ? 'font-semibold text-destructive' : 'text-muted-foreground/70'
              )}
            >
              {msg1Total} / {MSG1_MAX_CHARS}
            </span>
          }
          text={drafts.msg1}
          field="msg1"
          promptVersion={messagesQuery.data?.msg1PromptVersion}
          variants={variantsFor('msg1')}
          selectedArm={effectiveArm('msg1')}
          onSelectArm={(arm) => selectArm('msg1', arm)}
          onUseGenerated={hasCustom('msg1') ? () => void revertToGenerated('msg1') : undefined}
          resolve={resolve}
          editing={editing === 'msg1'}
          onToggleEdit={() => toggleEdit('msg1')}
          onChange={(value) => setDraft('msg1', value)}
          footer={
            msg1Over
              ? `Over LinkedIn's ${MSG1_MAX_CHARS}-char connection-note cap (your profile link takes ${msg1LinkLen} chars). It will be word-truncated when sent.`
              : undefined
          }
        />

        <ConversationDivider tone="accept">{firstName} accepts your request</ConversationDivider>

        <MessageBubble
          label="Follow-up · after they accept"
          text={drafts.msg2}
          field="msg2"
          promptVersion={messagesQuery.data?.msg2PromptVersion}
          variants={variantsFor('msg2')}
          selectedArm={effectiveArm('msg2')}
          onSelectArm={(arm) => selectArm('msg2', arm)}
          onUseGenerated={hasCustom('msg2') ? () => void revertToGenerated('msg2') : undefined}
          cta={msg2Cta}
          resolve={resolve}
          editing={editing === 'msg2'}
          onToggleEdit={() => toggleEdit('msg2')}
          onChange={(value) => setDraft('msg2', value)}
        />

        <ConversationDivider tone="muted">If no reply after a few days</ConversationDivider>

        <MessageBubble
          label="Second follow-up"
          text={drafts.msg3}
          field="msg3"
          promptVersion={messagesQuery.data?.msg3PromptVersion}
          variants={variantsFor('msg3')}
          selectedArm={effectiveArm('msg3')}
          onSelectArm={(arm) => selectArm('msg3', arm)}
          onUseGenerated={hasCustom('msg3') ? () => void revertToGenerated('msg3') : undefined}
          resolve={resolve}
          editing={editing === 'msg3'}
          onToggleEdit={() => toggleEdit('msg3')}
          onChange={(value) => setDraft('msg3', value)}
        />
      </div>
    </div>
  )
})

// An outgoing chat bubble that flips into an inline editor. The editor shows the
// raw template (tokens included); the preview substitutes and highlights the
// [firstName]/[Link] tokens so the founder sees the message as the investor
// receives it.
function MessageBubble({
  label,
  hint,
  text,
  field,
  promptVersion,
  cta,
  resolve,
  editing,
  onToggleEdit,
  onChange,
  footer,
  variants,
  selectedArm,
  onSelectArm,
  onUseGenerated,
}: {
  label: string
  hint?: ReactNode
  text: string
  /** Which touch this bubble renders — drives legacy link/CTA decoration. */
  field: OutreachMessageField
  /** Langfuse prompt version for this touch — decides legacy vs v2 rendering. */
  promptVersion?: string | null
  /** Separate legacy msg2 closing CTA, folded into the preview when present. */
  cta?: string | null
  /** Token values for the live preview (first name, link display). */
  resolve: { firstName?: string; fundName?: string; fundFocus?: string; link?: string }
  editing: boolean
  onToggleEdit: () => void
  onChange: (value: string) => void
  footer?: string
  /** Every live version of this message; absent or single = no toggle shown. */
  variants?: OutreachMessageVariant[]
  selectedArm?: string
  onSelectArm?: (arm: string) => void
  /** Discard the founder's edit for this field. Absent when there is none. */
  onUseGenerated?: () => void
}) {
  // Legacy (pre-v2) messages carry no greeting/link/CTA inline — the backend
  // adds them at push. Decorate them into the new-template shape for the preview
  // so old and new messages read the same. Legacy-vs-v2 is decided by the prompt
  // VERSION (mirrors the backend push), not the body. The editable draft stays raw.
  const previewText = isLegacyOutreachField(field, promptVersion, 'investor')
    ? decorateLegacyOutreachPreview(field, text, cta)
    : text
  return (
    <div className="flex flex-col items-end">
      <div className="mb-1 flex w-full max-w-[88%] items-center justify-between gap-3">
        <span className="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground/70">
          {label}
        </span>
        <span className="flex items-center gap-2 text-[11px]">
          {hint}
          {variants && selectedArm && onSelectArm && (
            <VariantToggle
              variants={variants}
              selected={selectedArm}
              onSelect={onSelectArm}
              onDropCustom={editing ? undefined : onUseGenerated}
            />
          )}
          <button
            type="button"
            onClick={onToggleEdit}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium transition-colors',
              editing ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {editing ? (
              <>
                <Check className="size-3" strokeWidth={2.5} />
                Done
              </>
            ) : (
              <>
                <Pencil className="size-3" />
                Edit
              </>
            )}
          </button>
        </span>
      </div>

      <div
        className={cn(
          'w-full max-w-[88%] rounded-2xl rounded-tr-sm border px-4 py-2.5',
          editing ? 'border-primary/50 bg-primary/5' : 'border-primary/20 bg-primary/10'
        )}
      >
        {editing ? (
          <Textarea
            value={text}
            onChange={(event) => onChange(event.target.value)}
            textareaSize="lg"
            autoFocus
            className="border-border/60 bg-background/60 text-[13px] leading-relaxed text-foreground/90"
          />
        ) : (
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/90">
            {text.trim() ? (
              renderTemplatePreview(previewText, resolve)
            ) : (
              <span className="italic text-muted-foreground/70">No message yet</span>
            )}
          </p>
        )}
      </div>

      {footer && (
        <p className="mt-1 w-full max-w-[88%] text-right text-[11px] leading-snug text-muted-foreground/80">
          {footer}
        </p>
      )}
    </div>
  )
}

function ConversationDivider({
  tone,
  children,
}: {
  tone: 'accept' | 'muted'
  children: ReactNode
}) {
  return (
    <div className="flex items-center justify-center gap-1.5 py-0.5 text-[11.5px] text-muted-foreground">
      {tone === 'accept' && <Check className="size-3 text-emerald-400" strokeWidth={3} />}
      {children}
    </div>
  )
}

function makeInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  )
}
